import type { INITIATIVES_QUERY_RESULT } from "@nnf3d/studio/types";
import { Link } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { m } from "motion/react";
import { useMemo } from "react";

import { Icon } from "../components/icon";
import { menuAnimation } from "../lib/const";
import { useGlobal } from "../lib/global-context";
import { useBackEscape } from "../lib/hooks/use-back-escape";
import { useIsMobile } from "../lib/hooks/use-breakpoints";
import { routeStore } from "../lib/store";
import type { VoidComponent } from "../lib/types";
import { MobileBreadcrumbs } from "./mobile-breadcrumbs";

interface Props {
	params: { pillar: string; category: string };
	data: INITIATIVES_QUERY_RESULT;
}

const CategoryRoute: VoidComponent<Props> = ({ params, data }) => {
	const { level, params: routeParams } = useStore(routeStore);

	useBackEscape({ to: "/$pillar", params });

	const { pillars, exploreInitiativesTitle } = useGlobal();
	const isMobile = useIsMobile();

	const pillar = useMemo(
		() => pillars.find((p) => p.slug.current === params.pillar),
		[pillars, params.pillar],
	);

	const category = useMemo(
		() => pillar?.categories.find((c) => c.slug.current === params.category),
		[pillar?.categories, params.category],
	);

	const initiatives = useMemo(
		() =>
			category?.initiatives
				.map((i) => data.find((initiative) => initiative._id === i._ref))
				.filter((i) => i !== undefined) ?? [],
		[category?.initiatives, data],
	);

	const initiativeItems = useMemo(
		() =>
			initiatives.map((initiative) => ({
				initiative,
				isActive: level === "initiative" && initiative.slug.current === routeParams.initiative,
				linkParams: {
					pillar: params.pillar,
					category: params.category,
					initiative: initiative.slug.current,
				},
			})),
		[initiatives, params.pillar, params.category, level, routeParams],
	);

	return (
		<>
			<MobileBreadcrumbs params={params} />

			<div className="scroll-container">
				<m.div
					{...menuAnimation(isMobile)}
					className="flex flex-1 flex-col justify-between gap-8 py-3 desktop:w-pane-small-content desktop:py-8"
				>
					<div className="flex flex-col gap-2 desktop:gap-3">
						{category?.title && (
							<div className="flex items-start gap-2">
								<h1 className="flex-1 text-xl text-foreground desktop:text-3xl">
									{category.title}
								</h1>
							</div>
						)}

						<div className="grid grid-rows-[1fr]">
							<div className="overflow-hidden">
								{category?.description && (
									<p className="font-serif text-sm text-foreground desktop:text-xl">
										{category.description}
									</p>
								)}
							</div>
						</div>
					</div>

					<div className="flex flex-col gap-3">
						<h2 className="text-xs text-foreground">{exploreInitiativesTitle}</h2>

						<ul className="flex flex-col text-foreground counter-reset">
							{initiativeItems.map(({ initiative, linkParams }) => (
								<li key={initiative._id}>
									<Link
										to="/$pillar/$category/$initiative"
										params={linkParams}
										className="progress-link flex min-h-18 items-start gap-4 py-3 desktop:min-h-24"
									>
										<span className="flex-1 text-sm desktop:text-xl">{initiative.title}</span>

										<Icon name="arrow-right" className="mt-0.5 size-5 shrink-0 desktop:mt-1" />
									</Link>
								</li>
							))}
						</ul>
					</div>
				</m.div>
			</div>
		</>
	);
};

export { CategoryRoute };
